import React, { useEffect, useState } from 'react';
import { useTournament } from '../hooks/useTournament';

const Ranking = () => {
  const { players, matchSchedule, getTeamPlayers, scoringConfig } = useTournament();
  const [blurAll, setBlurAll] = useState(false);
  // individualOverrides: players whose blur state is opposite of blurAll
  const [individualOverrides, setIndividualOverrides] = useState(new Set());

  // Get scoring values from config
  const { winPoints, drawPoints, goalScoredPoints, goalConcededPoints } = scoringConfig;

  useEffect(() => {
    if (individualOverrides.size > 0) {
      setIndividualOverrides(new Set());
    }
  }, [blurAll]);

  // Initialize stats for each player
  const stats = {};
  players.forEach(player => {
    stats[player] = { goals: 0, goalsConceded: 0, wins: 0, draws: 0, losses: 0, goalPoints: 0, bonusPoints: 0 };
  });

  // Calculate stats from matchSchedule
  matchSchedule.forEach((roundMatches, roundIndex) => {
    roundMatches.forEach(match => {
      const { teamA, teamB, scoreA, scoreB } = match;
      if (scoreA === null || scoreB === null) return;
      const isDraw = scoreA === scoreB;
      const bonusA = scoreA > scoreB ? winPoints : isDraw ? drawPoints : 0;
      const bonusB = scoreB > scoreA ? winPoints : isDraw ? drawPoints : 0;

      const teamAPlayers = getTeamPlayers(roundIndex, teamA);
      const teamBPlayers = getTeamPlayers(roundIndex, teamB);

      // Update team A players stats
      teamAPlayers.forEach(p => {
        stats[p].goals += scoreA;
        stats[p].goalsConceded += scoreB;
        stats[p].goalPoints += (scoreA * goalScoredPoints) + (scoreB * goalConcededPoints);
        stats[p].bonusPoints += bonusA;
        if (scoreA > scoreB) stats[p].wins += 1;
        else if (isDraw) stats[p].draws += 1;
        else stats[p].losses += 1;
      });

      // Update team B players stats
      teamBPlayers.forEach(p => {
        stats[p].goals += scoreB;
        stats[p].goalsConceded += scoreA;
        stats[p].goalPoints += (scoreB * goalScoredPoints) + (scoreA * goalConcededPoints);
        stats[p].bonusPoints += bonusB;
        if (scoreB > scoreA) stats[p].wins += 1;
        else if (isDraw) stats[p].draws += 1;
        else stats[p].losses += 1;
      });
    });
  });

  // Prepare sorted stats array
  const sorted = Object.entries(stats)
    .map(([player, s]) => ({ player, ...s, totalPoints: s.goalPoints + s.bonusPoints }))
    .sort((a, b) => b.totalPoints - a.totalPoints || b.goals - a.goals);

  // Toggle blur for individual player
  const togglePlayerBlur = (playerName) => {
    setIndividualOverrides(prev => {
      const next = new Set(prev);
      if (next.has(playerName)) {
        next.delete(playerName);
      } else {
        next.add(playerName);
      }
      return next;
    });
  };

  // Check if a player's name should be blurred
  const isPlayerBlurred = (playerName) => {
    return blurAll ? !individualOverrides.has(playerName) : individualOverrides.has(playerName);
  };

  return (
    <div className="section ranking">
      <div className="ranking-header">
        <h3>Spieler-Rangliste</h3>
        <label className="blur-toggle-label" title="Alle Namen verbergen/anzeigen">
          <input
            type="checkbox"
            className="blur-toggle-checkbox"
            checked={blurAll}
            onChange={(e) => setBlurAll(e.target.checked)}
          />
          <span className="blur-toggle-custom"></span>
          <span className="blur-toggle-text">Namen verbergen</span>
        </label>
      </div>
      <table className="tournament-table ranking-table">
        <thead>
          <tr>
            <th className="position-column">Nr.</th>
            <th>Spieler</th>
            <th>Tore</th>
            <th>Gegentore</th>
            <th>Siege</th>
            <th>Unent.</th>
            <th>Nied.</th>
            <th>Torpunkte</th>
            <th>Bonuspunkte</th>
            <th>Gesamt</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ player, goals, goalsConceded, wins, draws, losses, goalPoints, bonusPoints, totalPoints }, index) => (
            <tr key={player}>
              <td className="position-column">{index + 1}</td>
              <td className="player-name-cell">
                <button
                  type="button"
                  className="blur-row-toggle"
                  onClick={() => togglePlayerBlur(player)}
                  title={isPlayerBlurred(player) ? 'Namen anzeigen' : 'Namen verbergen'}
                >
                  {isPlayerBlurred(player) ? '👁️' : '🙈'}
                </button>
                <span className={`player-name-text ${isPlayerBlurred(player) ? 'blurred' : ''}`}>
                  {isPlayerBlurred(player) ? '██████████' : player}
                </span>
              </td>
              <td>{goals}</td>
              <td>{goalsConceded}</td>
              <td>{wins}</td>
              <td>{draws}</td>
              <td>{losses}</td>
              <td>{goalPoints}</td>
              <td>{bonusPoints}</td>
              <td>{totalPoints}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Ranking;